import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import OrderListRow from 'components/OrderListRow';

import { useDelete } from 'lib/graphql/useDelete';
import { useAvoidSuspense, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { useTableSort } from 'components/TableSorting';
import { usePagination } from 'components/Pagination';

import RefetchQuery, {
  OrderListRefetchQuery,
} from '__generated__/OrderListRefetchQuery.graphql';
import { OrderList_data$key } from '__generated__/OrderList_data.graphql';

type Props = {
  data: OrderList_data$key;
};

const OrderList: FC<Props> = props => {
  const { search } = useSearch();
  const { sort } = useTableSort();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    OrderListRefetchQuery,
    OrderList_data$key
  >(
    graphql`
      fragment OrderList_data on Query
      @refetchable(queryName: "OrderListRefetchQuery") {
        orders(first: $first, after: $after, sort: $sort, search: $search) {
          __id
          edges {
            node {
              id
              ...OrderListRow_order
            }
          }

          pageInfo {
            total

            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    RefetchQuery
  );

  const { __id, edges, pageInfo } = data.orders ?? {};

  const [deleteOrder, deleting] = useDelete(__id);

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
        sort,
      });
    }, [refetch, search, sort, variables])
  );

  return (
    <Table
      search
      pagination
      pageInfo={pageInfo ?? null}
      icon={<ShoppingCartIcon />}
      loading={loading || deleting}
      head={
        <TableRow>
          <TableHeadCell name="orderNr">Užsakymo numeris</TableHeadCell>

          <TableCell center>Laikas</TableCell>

          <TableHeadCell center name="status">
            Būsena
          </TableHeadCell>

          <TableHeadCell center name="owner">
            Pirkėjas
          </TableHeadCell>

          <TableHeadCell center name="pack">
            Kelionė
          </TableHeadCell>

          <TableHeadCell center name="priceTotal">
            Avansas / Pilna kaina, €
          </TableHeadCell>

          <TableHeadCell center name="adminComment">
            Tvarko
          </TableHeadCell>

          <TableCell></TableCell>
        </TableRow>
      }>
      {edges?.map(({ node }) => (
        <OrderListRow key={node.id} order={node} onDelete={deleteOrder} />
      ))}
    </Table>
  );
};

export default OrderList;
